"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const constants_1 = require("./constants");
const util_1 = require("../util");
// shared values
const INLINE_CONTENT = `${constants_1.NodeGroupMap.INLINE}*`;
/*

What follows is a list of all nodes classified by various factors.
The main reason for doing this is to be able to compose a schema on the fly,
so that we can control the default node via ordering.

*/
const docNodes = {
    [constants_1.NodeTypeMap.DOC]: {
        content: `${constants_1.NodeTypeMap.PAGE}+`,
        attrs: {
            docType: { default: constants_1.ScriptFormatMap.VARIETY },
        },
    },
    [constants_1.NodeTypeMap.PAGE]: {
        content: `(${constants_1.NodeGroupMap.BLOCK} | ${constants_1.NodeTypeMap.DUAL_DIALOGUE})+`,
        selectable: false,
        attrs: {
            dynamic: { default: false },
            locked: { default: false },
            pageNumber: { default: null },
        },
        parseDOM: [
            {
                tag: '.o-page',
                getAttrs(node) {
                    const opts = {};
                    // mark dynamic and locked pages on prose paste
                    if (node.classList.contains('o-page-dynamic')) {
                        opts.dynamic = true;
                    }
                    if (node.dataset.locked === 'true') {
                        opts.locked = true;
                    }
                    if (node.dataset.pageNumber !== null) {
                        opts.pageNumber = node.dataset.pageNumber;
                    }
                    return opts;
                },
            },
        ],
        toDOM(node) {
            return ['div', getDOMAttrs(node), 0];
        },
    },
};
const inlineNodes = {
    [constants_1.NodeTypeMap.TEXT]: {
        group: constants_1.NodeGroupMap.INLINE,
    },
    [constants_1.NodeTypeMap.HARD_BREAK]: {
        group: constants_1.NodeGroupMap.INLINE,
        inline: true,
        selectable: false,
        parseDOM: [{ tag: 'br' }],
        toDOM() {
            return ['br'];
        },
    },
};
const blockNodes = {
    // default block is first in group.
    // defaulting to dialogue for every doc type for now.
    [constants_1.NodeTypeMap.DIALOGUE]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: {
            elementNumber: { default: null },
            untimed: { default: null },
        },
        parseDOM: [{ tag: '.o-dialogue', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.ACTION]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: {
            alignment: { default: null },
        },
        parseDOM: [{ tag: '.o-action', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.SCENE_HEADING]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: {
            alignment: { default: null },
            elementNumber: { default: null },
        },
        parseDOM: [{ tag: '.o-scene', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.TRANSITION]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        parseDOM: [{ tag: '.o-transition', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.SLUG]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: { elementNumber: { default: null } },
        parseDOM: [{ tag: '.o-slug', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.BRACKET]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: {
            elementNumber: { default: null },
            untimed: { default: null },
        },
        parseDOM: [{ tag: '.o-bracket', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.CHARACTER]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: { untimed: { default: null } },
        parseDOM: [{ tag: '.o-character', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.PARENTHETICAL]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: { untimed: { default: null } },
        parseDOM: [{ tag: '.o-parenthetical', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.GENERAL]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        attrs: {
            alignment: { default: null },
            untimed: { default: null },
        },
        parseDOM: [{ tag: '.o-general', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.NEW_ACT]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        parseDOM: [{ tag: '.o-new-act', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
    [constants_1.NodeTypeMap.END_OF_ACT]: {
        group: constants_1.NodeGroupMap.BLOCK,
        content: INLINE_CONTENT,
        parseDOM: [{ tag: '.o-end-of-act', getAttrs: getProseAttrs }],
        toDOM: blockToDOM,
    },
};
const dualNodes = {
    [constants_1.NodeTypeMap.DUAL_DIALOGUE]: {
        content: `${constants_1.NodeTypeMap.DUAL_DIALOGUE_COLUMN}{2}`,
        attrs: {
            elementNumber: { default: null },
            id: { default: null },
        },
        parseDOM: [{ tag: '.o-dual-dialogue', getAttrs: getProseAttrs }],
        toDOM(node) {
            return ['div', getDOMAttrs(node), 0];
        },
    },
    [constants_1.NodeTypeMap.DUAL_DIALOGUE_COLUMN]: {
        content: `${constants_1.NodeTypeMap.CHARACTER} (${constants_1.NodeTypeMap.DIALOGUE} | ${constants_1.NodeTypeMap.PARENTHETICAL})*`,
        parseDOM: [{ tag: '.o-dual-dialogue__column' }],
        toDOM() {
            return ['div', { class: 'o-dual-dialogue__column' }, 0];
        },
    },
};
/*

!!! BLOCK NODE IDs !!!
Adding an `id` attr to _all_ block-level nodes.
Defaults to null.
ID must be assigned after creation using appendTransaction.
(b/c of ProseMirror design quirks)
See https://discuss.prosemirror.net/t/pass-custom-id-to-nodes/2225

*/
Object.keys(blockNodes).forEach((nodeType) => {
    const spec = blockNodes[nodeType];
    if (!spec.attrs) {
        spec.attrs = {};
    }
    spec.attrs.id = { default: null };
});
/*

Here we compose all nodes into a single schema for all different doc types.
After document nodes and inline nodes, it's important for the first block node
to be the _default node_, that is to say the node that will show up if the user
deletes everything or manages to sidestep node prediction (enter/tab/etc).

For simplicity's sake we're using GENERAL as the default node for both doc types,
and relying on good enter and tab handling to dictate what shows up for users
when writing in screenplay and variety docs.

*/
const nodes = Object.assign({}, docNodes, inlineNodes, blockNodes, dualNodes);
// UTILITY FUNCTIONS
/**
 * shared toDOM method for all block nodes
 * @param {Node} node - prosemirror node
 * @returns {array} - PM dom representation
 */
function blockToDOM(node) {
    return ['p', getDOMAttrs(node), 0];
}
/**
 * NOT IN USE -- need to prevent duplicate IDs in PM ID plugin
 * create attrs for dom node with id
 * @param {Element} domNode - html dom node
 * @return {object?} node attrs
 */
// function getId (domNode) {
//   const id = domNode.id
//   if (id != null) return { id }
//   // return undefined for getAttrs to fallback to defaults
// }
// reverse if needed
// function kebabToCamel (str) {
//   return str.replace(/-([a-z])/g, m => m[1].toUpperCase())
// }
/**
 * get all DOM attributes for any node
 * @param {Node} node - prose node
 * @return {object} dom attrs
 */
function getDOMAttrs(node) {
    const { type, attrs } = node;
    const { name, spec } = type;
    const domAttrs = {};
    // 1. class name
    if (name === constants_1.NodeTypeMap.SCENE_HEADING) {
        domAttrs.class = 'o-scene'; // special case :( TODO: make unspecial
    }
    else {
        domAttrs.class = `o-${name.replace(/_/g, '-')}`;
    }
    // inlined style for portablity to other rich text editors
    if (name === constants_1.NodeTypeMap.BRACKET) {
        domAttrs.style = 'color: #cd3c8c;';
    }
    // 2. group class
    // add class for block elements
    // https://prosemirror.net/docs/ref/#model.NodeSpec.group
    if (typeof spec.group === 'string' &&
        spec.group.includes(constants_1.NodeGroupMap.BLOCK)) {
        domAttrs.class += ' o-block-element';
    }
    // 3. alignment
    // add class for alignment (deprecate?)
    if (attrs.alignment) {
        domAttrs.class += ` is-${attrs.alignment}-aligned`;
    }
    // 4. dynamic class (page only)
    if (name === constants_1.NodeTypeMap.PAGE && node.attrs.dynamic) {
        domAttrs.class += ' o-page-dynamic';
    }
    // 5. id
    if (attrs.id) {
        domAttrs.id = node.attrs.id;
    }
    // 6. data attrs
    // convert all node attrs to data attrs (unless null, undefined, or false)
    Object.keys(attrs).forEach((key) => {
        // ID is assigned to regular DOM ID attr above
        // timing and alignment are assigned as classnames
        if (['id', 'untimed', 'alignment'].includes(key)) {
            return;
        }
        if (attrs[key] == null || attrs[key] === false) {
            return;
        }
        domAttrs[`data-${(0, util_1.camelToKebab)(key)}`] = attrs[key];
    });
    // 7. exclude from timing
    if (attrs.untimed != null) {
        domAttrs.class += ' is-untimed';
    }
    return domAttrs;
}
/**
 * get all attributes defined in our schema for any DOM node
 * @param {Node} node - prose node
 * @return {object} dom attrs
 */
function getProseAttrs(domNode) {
    let attrs = {};
    const match = domNode.className.match(/is-([a-z]+)-aligned/);
    if (match !== null) {
        attrs.alignment = match[1];
    }
    if (domNode.classList.contains('is-untimed')) {
        attrs.untimed = true;
    }
    if (domNode.dataset.elementNumber !== undefined) {
        attrs.elementNumber = domNode.dataset.elementNumber;
    }
    // we pass through this unique attribute during cut/paste because otherwise moving a slug gives it a new id and confuses prompter software.
    // during copy/paste (when the existing block is still present in the doc too), the idMaker PM plugin will assign something unique in a follow up transaction to ensure uniqueness
    const { id } = domNode;
    if ((0, util_1.isValidId)(id)) {
        attrs.id = id;
    }
    return attrs;
}
exports.default = nodes;
